import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { Users, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import api from '../../lib/api';

const ROLES = ['Prod', 'Sales', 'Both', 'Other', 'Inactive'];
const PAYMENT_METHODS = [
  { value: 'wise', label: 'Wise' },
  { value: 'alleanza', label: 'Alleanza (US Payroll)' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'email_only', label: 'Email Only' },
  { value: 'owner_draw', label: 'Owner Draw' },
];
const PAY_PERIODS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];
const ROLE_COLORS = {
  Prod: 'bg-blue-50 text-blue-700 border-blue-200',
  Sales: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Both: 'bg-violet-50 text-violet-700 border-violet-200',
  Other: 'bg-gray-50 text-gray-600 border-gray-200',
  Inactive: 'bg-red-50 text-red-500 border-red-200',
};

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(v) || 0);

export default function StaffModal({ open, onOpenChange }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/ops/staff');
      setStaff(res.data.data || []);
    } catch {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleFieldUpdate = async (email, field, value) => {
    setUpdating(`${email}-${field}`);
    try {
      await api.patch(`/api/ops/staff/${encodeURIComponent(email)}`, { [field]: value });
      setStaff(prev => prev.map(s => s.email === email ? { ...s, [field]: value } : s));
      const label = field === 'role' ? value : `${field}: ${value}`;
      toast.success(`${email.split('@')[0]} → ${label}`);
    } catch {
      toast.error(`Failed to update ${field}`);
    } finally {
      setUpdating(null);
    }
  };

  const handleStdHoursBlur = (email, rawValue) => {
    const parsed = parseFloat(rawValue);
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 168) {
      handleFieldUpdate(email, 'standard_hours', parsed);
    }
  };

  const handlePayrollEmailBlur = (email, newVal, original) => {
    const trimmed = (newVal || '').trim().toLowerCase();
    if (trimmed && trimmed !== (original || '').toLowerCase()) {
      handleFieldUpdate(email, 'payroll_email', trimmed);
    }
  };

  const handleWiseIdBlur = (email, newVal, original) => {
    const trimmed = (newVal || '').trim();
    if (trimmed !== (original || '').trim()) {
      handleFieldUpdate(email, 'wise_recipient_id', trimmed);
    }
  };

  // Sort: active first, inactive at bottom
  const sorted = [...staff].sort((a, b) => {
    if (a.role === 'Inactive' && b.role !== 'Inactive') return 1;
    if (a.role !== 'Inactive' && b.role === 'Inactive') return -1;
    return (a.name || '').localeCompare(b.name || '');
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl p-0 gap-0 flex flex-col" style={{ maxHeight: '80vh' }}>
        <DialogHeader className="px-6 pt-5 pb-3 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2" data-testid="staff-modal-title">
            <Users className="h-5 w-5 text-primary" />
            Staff Management
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Manage roles, standard hours, and rates. Set role to "Inactive" to hide from payroll.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Loading staff...
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Name</TableHead>
                  <TableHead className="text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground w-[80px]">Rate</TableHead>
                  <TableHead className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground w-[90px]">Std Hours</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wide text-muted-foreground w-[90px]">Cycle</TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wide text-muted-foreground w-[130px]">Role</TableHead>
                  <TableHead className="text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground w-[70px]">SSN-4</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map(s => (
                  <TableRow
                    key={s.email}
                    data-testid={`staff-row-${s.email}`}
                    className={s.role === 'Inactive' ? 'opacity-50' : ''}
                  >
                    <TableCell>
                      <span className="text-sm font-medium">{s.name}</span>
                      <span className="text-[10px] text-muted-foreground block leading-tight">{s.email}</span>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-[9px] text-muted-foreground/70 whitespace-nowrap">Payroll:</span>
                        <Input
                          type="email"
                          className="h-5 text-[10px] px-1.5 py-0 border-dashed max-w-[160px]"
                          placeholder="personal@email.com"
                          data-testid={`payroll-email-${s.email}`}
                          defaultValue={s.payroll_email || s.email}
                          disabled={updating === `${s.email}-payroll_email`}
                          onBlur={(e) => handlePayrollEmailBlur(s.email, e.target.value, s.payroll_email || s.email)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[9px] text-muted-foreground/70 whitespace-nowrap">Pay via:</span>
                        <Select
                          value={s.payment_method || 'wise'}
                          onValueChange={(v) => handleFieldUpdate(s.email, 'payment_method', v)}
                          disabled={updating === `${s.email}-payment_method`}
                        >
                          <SelectTrigger className="h-5 text-[10px] px-1.5 py-0 border-dashed w-[100px]" data-testid={`payment-method-${s.email}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAYMENT_METHODS.map(pm => (
                              <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {(s.payment_method || 'wise') === 'wise' && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[9px] text-muted-foreground/70 whitespace-nowrap">Wise ID:</span>
                          <Input
                            type="text"
                            className="h-5 text-[10px] px-1.5 py-0 border-dashed max-w-[120px] font-mono bg-muted/30"
                            placeholder="Auto-generates"
                            data-testid={`wise-id-${s.email}`}
                            value={s.wise_recipient_id || ''}
                            readOnly
                            tabIndex={-1}
                          />
                          {!s.wise_recipient_id && <span className="text-[8px] text-muted-foreground/50 italic">on first pay</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm" data-role="mono">{fmt(s.pay_rate)}/hr</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Input
                        type="number"
                        min="0"
                        max="168"
                        step="1"
                        className="h-7 w-[70px] text-xs text-center mx-auto"
                        data-testid={`std-hours-${s.email}`}
                        defaultValue={s.standard_hours || 40}
                        disabled={updating === `${s.email}-standard_hours`}
                        onBlur={(e) => handleStdHoursBlur(s.email, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={s.pay_period || 'weekly'}
                        onValueChange={(v) => handleFieldUpdate(s.email, 'pay_period', v)}
                        disabled={updating === `${s.email}-pay_period`}
                      >
                        <SelectTrigger className="h-7 text-xs w-[90px]" data-testid={`pay-period-${s.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAY_PERIODS.map(pp => (
                            <SelectItem key={pp.value} value={pp.value}>{pp.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={s.role}
                        onValueChange={(v) => handleFieldUpdate(s.email, 'role', v)}
                        disabled={updating === `${s.email}-role`}
                      >
                        <SelectTrigger className="h-7 text-xs w-[110px]" data-testid={`role-select-${s.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map(r => (
                            <SelectItem key={r} value={r}>
                              <span className={r === 'Inactive' ? 'text-red-500' : ''}>{r}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-center">
                      <input
                        type="text"
                        maxLength={4}
                        className="h-7 w-[55px] text-xs text-center border rounded bg-background"
                        defaultValue={s.last_4_ssn || ''}
                        placeholder="••••"
                        disabled={updating === `${s.email}-last_4_ssn`}
                        onBlur={(e) => {
                          const val = e.target.value.trim();
                          if (val !== (s.last_4_ssn || '')) {
                            handleFieldUpdate(s.email, 'last_4_ssn', val);
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <p className="text-[10px] text-muted-foreground">
              {staff.filter(s => s.role !== 'Inactive').length} active · {staff.filter(s => s.role === 'Inactive').length} inactive
            </p>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="staff-modal-close">Close</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
